/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line no-restricted-imports
import * as React from 'react';

import { cssMap, cx, jsx } from '@compiled/react';

import UserAvatarCircleIcon from '@atlaskit/icon/core/person-avatar';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { useImageState } from '../../hooks/use-image-state';

export type ProfilePictureProps = {
	/**
	 * An optional overlay to render on top of the avatar.
	 * Consumers are responsible for positioning the overlay absolutely within the
	 * relative-positioned wrapper that is rendered when this prop is provided.
	 */
	avatarOverlay?: React.ReactNode;
	src: string | undefined;
	testId?: string;
};

const styles = cssMap({
	imgContainer: {
		width: '64px',
		height: '64px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& svg, & img': {
			width: '64px',
			height: '64px',
		},
	},
	imgContainerSkeleton: {
		borderRadius: token('radius.full'),
		backgroundColor: token('color.background.neutral'),
	},
	img: {
		display: 'block',
		borderRadius: token('radius.full'),
	},
	overlayWrapper: {
		position: 'relative',
		display: 'inline-block',
	},
});

export const ProfilePicture: React.ComponentType<ProfilePictureProps> = ({
	testId,
	src,
	avatarOverlay,
}) => {
	const state = useImageState(src || '');

	const displayImage = src && state === 'loaded';
	const displayFallback = src && state === 'error';
	const displaySkeleton = !displayImage && !displayFallback;

	return (
		<Box xcss={avatarOverlay ? styles.overlayWrapper : undefined}>
			<Box
				testId={testId}
				xcss={cx(styles.imgContainer, displaySkeleton && styles.imgContainerSkeleton)}
			>
				{displayImage && (
					<img data-testid={testId && `${testId}--img`} src={src} css={styles.img} alt="" />
				)}
				{displayFallback && (
					<UserAvatarCircleIcon
						spacing="spacious"
						testId={testId && `${testId}--fallback`}
						label=""
						color={token('color.text.subtlest')}
					/>
				)}
			</Box>
			{avatarOverlay}
		</Box>
	);
};
